<script lang="ts" setup>
import {computed, ref, watch} from "vue";
import Modal from "@src/components/ui/utils/Modal.vue";
import ScrollBoxTransferModal from "@src/components/ui/utils/ScrollBoxTransferModal.vue";
import {db} from "@src/services/db";
import type {Tag} from "@src/services/tags";
import {useSessionTags} from "@src/composables/useSessionTags";
import {useChatSessionStore} from "@src/stores/chatSession";

const show = ref(false);
const allTags = ref<Tag[]>([]);

const {selectedTags, toggleTag, isPending} = useSessionTags();
const sessionStore = useChatSessionStore();

const selectedDepartmentId = computed(() => {
    return (
        sessionStore.selectedSession?.department?.id ??
        null
    );
});

const availableTags = computed(() => {
    return allTags.value
        .filter((tag: Tag) => {
            return (
                tag.department_id === null ||
                typeof tag.department_id === "undefined" ||
                Number(tag.department_id) === Number(selectedDepartmentId.value)
            );
        })
        .sort((a: Tag, b: Tag) => (a.text ?? "").localeCompare(b.text ?? ""));
});

function isSelected(tag: Tag) {
    return selectedTags.value.some(t => t.id === tag.id);
}

async function loadTagsFromDB() {
    const tags = await (db as any).tags?.toArray?.();
    allTags.value = (tags ?? []).filter((t: Tag) => t.status !== "inactive").sort((a: Tag, b: Tag) => (a.text ?? '').localeCompare(b.text ?? ''));
}

async function toggle(tag: Tag) {
    await toggleTag(tag);
}

watch(
    () => show.value,
    async (open) => {
        if (!open) return;
        await loadTagsFromDB();
    }
);
</script>

<template>
    <div class="flex flex-nowrap items-center gap-2 max-w-full overflow-x-auto scrollbar-hidden">
        <span class="font-normal text-gray-400 text-sm/4 flex-shrink-0">Tags:</span>

        <!-- tags selecionadas -->
        <button
            v-for="t in selectedTags"
            :key="t.id"
            :disabled="isPending(t.id)"
            :style="{ backgroundColor: t.background_color, color: t.text_color }"
            class="text-xs px-3 py-1 rounded-full flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
            title="Remover tag"
            @click="toggle(t)"
        >
            {{ t.text }}
            <span class="opacity-80">✕</span>
        </button>

        <!-- botão adicionar -->
        <button
            class="text-xs px-3 py-1 rounded-full cursor-pointer bg-black/5 dark:bg-white/10 text-black/70 dark:text-white/70 hover:bg-black/10 dark:hover:bg-white/20 flex-shrink-0 whitespace-nowrap"
            @click="show = true"
        >
            + Adicionar
        </button>

        <Modal :close-modal="() => (show = false)" :open="show">
            <template v-slot:content>
                <div class="w-[95vw] sm:w-[30rem] max-w-[30rem] bg-white dark:bg-gray-900 rounded pt-6 shadow">
                    <div class="flex justify-between items-center mb-4 px-5">
                        <p class="heading-1 text-black/70 dark:text-white/70">Gerenciar tags</p>
                    </div>

                    <div class="px-5 pb-5">
                        <ScrollBoxTransferModal
                            class="overflow-y-auto max-h-72 border border-black/5 dark:border-white/10 rounded">
                            <div v-if="availableTags.length === 0" class="p-4 text-sm text-black/60 dark:text-white/60">
                                Nenhuma tag encontrada.
                            </div>

                            <button
                                v-for="t in availableTags"
                                :key="t.id"
                                :disabled="isPending(t.id)"
                                class="w-full text-left px-4 py-3 border-b border-black/5 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5 flex items-center justify-between"
                                @click="toggle(t)"
                            >
                                <span
                                    :style="{ backgroundColor: t.background_color, color: t.text_color }"
                                    class="text-xs px-3 py-1 rounded-full"
                                >
                                  {{ t.text }}
                                </span>

                                <span
                                    :class="isSelected(t) ? 'text-red-700 text-xs' : 'text-green-700 text-xs'"
                                    class="cursor-pointer">
                                  {{ isSelected(t) ? "Remover" : "Adicionar" }}
                                </span>
                            </button>
                        </ScrollBoxTransferModal>

                        <div class="flex gap-3 pt-5 justify-end">
                            <Button
                                class="text-sm justify-center cursor-pointer rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-3 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-500"
                                @click="show = false"
                            >
                                Fechar
                            </Button>
                        </div>
                    </div>
                </div>
            </template>
        </Modal>
    </div>
</template>
